import { useEffect } from 'react';
import type { RefObject } from 'react';
import { dismissOrderToast } from '../components/activity/ActivityToast';
import {
  incrementOrderVisible,
  decrementOrderVisible,
  registerOrderCardEl,
  unregisterOrderCardEl,
} from '../components/activity/orderVisibility';

// Fraction of the element that must be in the viewport to count as "seen".
const VISIBLE_RATIO = 0.5;
// How long it must stay visible before we treat it as seen and dismiss the
// toast — guards against a fast scroll-past silently killing a toast.
const DWELL_MS = 300;

/**
 * Tracks whether an order's element is actually on screen (IntersectionObserver,
 * ≥50% visible), regardless of when it mounted or whether it was scrolled to.
 *
 *  - While on screen it's counted in the visibility registry, which the toast
 *    layer reads (`isOrderVisible`) to avoid making a toast sticky for an order
 *    the user is already looking at.
 *  - Once it has been visible for a short dwell, any toast for that order is
 *    dismissed (the user has clearly seen it).
 */
export function useOrderVisibility(
  orderId: string,
  ref: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || !orderId) return;

    // Register the element so a toast tap can scroll to this card in place.
    registerOrderCardEl(orderId, el);

    let counted = false;
    let dwellTimer: ReturnType<typeof setTimeout> | null = null;
    const clearDwell = () => {
      if (dwellTimer) {
        clearTimeout(dwellTimer);
        dwellTimer = null;
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible =
          entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO;
        if (visible && !counted) {
          counted = true;
          incrementOrderVisible(orderId);
          dwellTimer = setTimeout(() => dismissOrderToast(orderId), DWELL_MS);
        } else if (!visible && counted) {
          counted = false;
          clearDwell();
          decrementOrderVisible(orderId);
        }
      },
      { threshold: [VISIBLE_RATIO] },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      clearDwell();
      if (counted) decrementOrderVisible(orderId);
      unregisterOrderCardEl(orderId, el);
    };
  }, [orderId, ref]);
}
